/**
 * Zaman ile ilgili işlemleri yöneten servis
 *
 * NOT (Faz 5 DILIM 7): RTDB tabanli haftalik kacirilan gorev tarama metodlari
 * (checkWeeklyMissedTasks / checkAllCompaniesWeeklyMissedTasks) kaldirildi —
 * bu mantik artik backend cron'unda (gts_task_api). Burada yalniz saf zaman
 * yardimcilari kaldi (Firebase bagimliligi yok).
 */
class TimeService {
  // Singleton yapısı
  private static instance: TimeService;
  
  private constructor() {}
  
  public static getInstance(): TimeService {
    if (!TimeService.instance) {
      TimeService.instance = new TimeService();
    }
    return TimeService.instance;
  }
  
  /**
   * Sistemin şu anki zamanını alır
   * @returns Şu anki yerel zamanı döndürür
   */
  public async getCurrentTime(): Promise<Date> {
    // Firebase zamanı kullanmak yerine doğrudan yerel zamanı döndür
    return new Date();
  }
  
  /**
   * Belirli bir saatin geçip geçmediğini kontrol eder
   * @param timeString "14:30" formatında zaman
   * @returns Zaman geçtiyse true, geçmediyse false
   */
  public async isTimePassed(timeString: string): Promise<boolean> {
    try {
      // Zaman formatını ayrıştır (14:30 gibi)
      const parts = timeString.split(':');
      const hour = parseInt(parts[0], 10);
      const minute = parseInt(parts[1], 10);
      
      // Şu anki zamanı al
      const now = await this.getCurrentTime();
      
      // Karşılaştırılacak zamanı oluştur
      const taskTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hour,
        minute
      );
      
      // Şu anki zaman, görev zamanından sonra mı kontrol et
      return now > taskTime;
    } catch (error) {
      console.error('Zaman kontrolü yapılamadı:', error);
      return false;
    }
  }
  
  /**
   * Bir zamanın yaklaşıp yaklaşmadığını kontrol eder
   * @param timeString "14:30" formatında zaman
   * @param toleranceMinutes Tolerans süresi (dakika)
   * @returns Zaman yaklaşıyorsa true, yaklaşmıyorsa false
   */
  public async isTimeApproaching(timeString: string, toleranceMinutes: number): Promise<boolean> {
    try {
      // Zaman formatını ayrıştır (14:30 gibi)
      const parts = timeString.split(':');
      const hour = parseInt(parts[0], 10);
      const minute = parseInt(parts[1], 10);
      
      // Şu anki zamanı al
      const now = await this.getCurrentTime();
      
      // Karşılaştırılacak zamanı oluştur
      const taskTime = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        hour,
        minute
      );
      
      // Tolerans başlangıcını hesapla (görevden önce)
      const toleranceStart = new Date(taskTime.getTime());
      toleranceStart.setMinutes(toleranceStart.getMinutes() - toleranceMinutes);
      
      // Şu anki zaman, tolerans başlangıcı ile görev zamanı arasında mı kontrol et
      return now > toleranceStart && now < taskTime;
    } catch (error) {
      console.error('Zaman yaklaşma kontrolü yapılamadı:', error);
      return false;
    }
  }
  
  /**
   * Bugünün tarihini YYYY-MM-DD formatında döndürür
   * @returns YYYY-MM-DD formatında tarih
   */
  public getTodayDateKey(): string {
    const now = new Date();
    return [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0')
    ].join('-');
  }
  
  /**
   * Zaman string'ini DateTime objesine dönüştürür (bugünün tarihi ile)
   * @param timeString "14:30" formatında zaman
   * @returns DateTime objesi
   */
  public timeStringToDateTime(timeString: string): Date {
    const parts = timeString.split(':');
    const hour = parseInt(parts[0], 10);
    const minute = parseInt(parts[1], 10);
    
    const now = new Date();
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      hour,
      minute
    );
  }

  /**
   * Belirli bir tarihe ait haftanın gününü belirler
   * @param date Tarih objesi
   * @returns 0-6 arasında gün numarası (0=Pazar, 1=Pazartesi, ...)
   */
  public getDayOfWeek(date: Date): number {
    return date.getDay(); // 0=Pazar, 1=Pazartesi, ... 6=Cumartesi
  }

  /**
   * Belirli bir tarih için oluşturulmuş tarih anahtarını döndürür
   * @param date Tarih objesi
   * @returns YYYY-MM-DD formatında tarih
   */
  public getDateKey(date: Date): string {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-');
  }
}

// Singleton olarak dışa aktar
export default TimeService.getInstance(); 
